/**
 * The one place an HTTP request leaves the browser.
 *
 * Two things it exists to get right, both of which are easy to get wrong once and
 * then repeat in forty call sites:
 *
 * 1. The access token lives in memory here and nowhere else. Not localStorage —
 *    a token in localStorage is readable by any injected script. The refresh
 *    token is in an HttpOnly cookie the browser attaches on its own and this
 *    code cannot see.
 *
 * 2. A 401 triggers exactly one refresh, however many requests hit it at once.
 *    A page issuing six parallel queries would otherwise fire six refreshes, and
 *    since refresh tokens rotate with reuse detection, five of them look like a
 *    stolen token being replayed — the server would revoke the whole family and
 *    sign the user out for loading a page.
 */

export class ApiError extends Error {
  // Declared and assigned explicitly rather than as constructor parameter
  // properties: the template sets `erasableSyntaxOnly`, which forbids any
  // TypeScript syntax that emits runtime code.
  readonly status: number
  readonly title: string
  readonly detail?: string
  /** Anything else the problem carried — the margin checks on a 409, for one. */
  readonly extensions: Record<string, unknown>

  constructor(status: number, title: string, detail?: string, extensions: Record<string, unknown> = {}) {
    super(detail ? `${title}: ${detail}` : title)
    this.name = 'ApiError'
    this.status = status
    this.title = title
    this.detail = detail
    this.extensions = extensions
  }
}

type ProblemDetails = { title?: string; detail?: string; status?: number; [key: string]: unknown }

let accessToken: string | null = null
let onSignedOut: (() => void) | null = null

/** In-flight refresh, shared by every caller that arrives while it runs. */
let refreshInFlight: Promise<boolean> | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

/** Called when the session cannot be recovered, so the app can route to login. */
export function setSignedOutHandler(handler: () => void): void {
  onSignedOut = handler
}

/**
 * Exchanges the refresh cookie for a new access token.
 *
 * Concurrent callers share one promise. `finally` clears it so a later 401 can
 * start a fresh attempt rather than resolving against a stale result.
 */
export async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The body is empty: the browser's refresh token is in the cookie it
        // attaches automatically and cannot read.
        body: '{}',
        credentials: 'same-origin',
      })

      if (!response.ok) {
        accessToken = null
        return false
      }

      const payload = (await response.json()) as { accessToken: string }
      accessToken = payload.accessToken
      return true
    } catch {
      accessToken = null
      return false
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

type RequestOptions = {
  method?: string
  body?: unknown
  /** Skip the refresh-and-retry dance — used by the auth calls themselves. */
  anonymous?: boolean
  signal?: AbortSignal
  /** Makes a POST safe to retry; the server returns the original response. */
  idempotencyKey?: string
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await authorisedFetch(path, options)

  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}

async function authorisedFetch(path: string, options: RequestOptions): Promise<Response> {
  // A FormData body goes as-is: the browser writes the multipart boundary into the
  // Content-Type itself, and setting the header by hand drops the boundary and
  // leaves the server unable to read a single field.
  const isForm = typeof FormData !== 'undefined' && options.body instanceof FormData

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = isForm ? {} : { 'Content-Type': 'application/json' }

    if (accessToken && !options.anonymous) {
      headers.Authorization = `Bearer ${accessToken}`
    }

    if (options.idempotencyKey) {
      headers['Idempotency-Key'] = options.idempotencyKey
    }

    return fetch(path, {
      method: options.method ?? 'GET',
      headers,
      body:
        options.body === undefined
          ? undefined
          : isForm
            ? (options.body as FormData)
            : JSON.stringify(options.body),
      credentials: 'same-origin',
      signal: options.signal,
    })
  }

  let response = await send()

  if (response.status === 401 && !options.anonymous) {
    const recovered = await refreshSession()

    if (!recovered) {
      onSignedOut?.()
      throw new ApiError(401, 'Session expired', 'Sign in again.')
    }

    response = await send()
  }

  if (!response.ok) {
    // The API returns ProblemDetails, but a proxy or a crash may not — never
    // assume the error body is JSON, or the real failure is hidden behind a
    // parse error.
    let problem: ProblemDetails = {}
    try {
      problem = (await response.json()) as ProblemDetails
    } catch {
      problem = { title: response.statusText || 'Request failed' }
    }

    const { title, detail, status: _status, type: _type, ...extensions } = problem
    void _status
    void _type

    throw new ApiError(response.status, title ?? 'Request failed', detail, extensions)
  }

  return response
}

/** A fresh idempotency key for a document-creating POST. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}
